import { useQuery } from '@apollo/client';
import {
  AssetAssetType,
  GET_WATCHLIST_SECURITIES_QUERY,
  GetWatchlistSecuritiesQuery,
} from '@fintronners/react-api/src';
import { WatchlistServiceApi, WatchlistServiceSetBody } from '@fintronners/react-api/src/tsoai';
import { useGrpcApiV1Config } from './useGrpcApiV1Config';
import RuntimeEnvGRPCAPI from '@/utils/RuntimeEnvGRPCAPI';
import { Configuration } from '@fintronners/react-api/src';
import fLogger from '@fintronners/react-utils/src/fLogger';

const useWatchlist = (assetId?: string) => {
  const { data, networkStatus, refetch, loading } = useQuery<GetWatchlistSecuritiesQuery>(
    GET_WATCHLIST_SECURITIES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
    },
  );

  const { basePath, accessToken } = useGrpcApiV1Config();

  const watchlistApiService = RuntimeEnvGRPCAPI.getV1Service(
    WatchlistServiceApi,
    new Configuration({
      basePath,
      accessToken,
      baseOptions: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    }),
  );

  const watchlist = data?.watchlists?.[0]?.list ?? [];
  const assetIsInWatchlist = watchlist.some((asset) => asset.id === assetId);

  const updateWatchlist = async (updatedWatchlist: { id: string; type: AssetAssetType }[]) => {
    const watchlistServiceSetRequest: WatchlistServiceSetBody = {
      assetUids: updatedWatchlist.map((item) => {
        return {
          uid: item.id,
          type: item.type,
        };
      }),
    };

    try {
      await watchlistApiService.watchlistServiceSet('', watchlistServiceSetRequest);
      refetch();
    } catch (err) {
      fLogger.error('Error updating watchlist');
    }
  };

  const addToWatchlist = () => {
    const addedWatchlist = [
      ...watchlist,
      { id: assetId ?? '', type: AssetAssetType.AssetTypeSecurity },
    ];
    updateWatchlist(addedWatchlist);
  };

  const removeFromWatchlist = () => {
    const filteredWatchlist = watchlist.filter((item) => item.id !== assetId);
    updateWatchlist(filteredWatchlist);
  };

  return {
    watchlist,
    assetIsInWatchlist,
    networkStatus,
    addToWatchlist,
    removeFromWatchlist,
    loading,
  };
};

export default useWatchlist;
